import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { CustomInnerHeaderProps } from "ag-grid-react";

export function HeaderCellMenu({
    children,
}: CustomInnerHeaderProps & {
    children?: React.ReactNode;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const menuRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);

    const toggleMenu = () => {
        if (!isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setMenuPosition({
                top: rect.bottom + 4,
                left: window.innerWidth - rect.right,
            });
        }
        setIsOpen(!isOpen);
    };

    useEffect(() => {
        if (!children) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, children]);

    const dropdownMenu =
        children && isOpen ? (
            <div
                ref={menuRef}
                className={clsx(
                    "bg-slate-800",
                    "rounded-lg shadow-400",
                    "custom-scrollbar max-h-[400px] overflow-auto p-2",
                    "fixed z-[10000] mt-1",
                    "min-w-[160px]",
                    "flex flex-col gap-1",
                )}
                style={{
                    top: `${menuPosition.top}px`,
                    right: `${menuPosition.left}px`,
                }}
            >
                {children}
            </div>
        ) : null;

    return (
        <>
            <div
                onClick={toggleMenu}
                ref={buttonRef}
                className="cursor-pointer"
            >
                <EllipsisVerticalIcon className="size-5" />
            </div>

            {dropdownMenu && createPortal(dropdownMenu, document.body)}
        </>
    );
}
